/**
 * Real games on Lichess, played on this site's board (Phase 3a).
 *
 * `lichess-board.js` is the Board API and the rules; this is the page. It has
 * three states: not connected (one button), the lobby (find a game, challenge
 * somebody, what is waiting for you), and a game.
 *
 * LICHESS OWNS THE GAME, as the relay does on the friend page: a click here
 * sends the move, and the board plays it only when Lichess's game stream says
 * it happened. So the board can never show a move Lichess did not accept.
 *
 * NO HELP DURING A GAME. The board is `fair` (no forces), no engine is loaded,
 * and the review is offered only once the game is over — the same rule Lichess
 * has for its own analysis.
 *
 * TWO STREAMS STAY OPEN: the account's events (games starting and ending,
 * challenges), from the moment the page is connected, and the game being
 * played. A seek or a sent challenge is a third, which Lichess keeps alive only
 * while it is open — so Stop and Cancel simply close it.
 */

import { createTable, escapeHtml } from './table.js';
import { uciOf } from './live-game.js';
import { mountNav } from '../nav.mjs';
import { mountInstall } from '../install.js';
import { mountCameraMenu, CAMERA_WORDS } from '../camera-menu.js';
import { mountLeanLayout } from '../lean-layout.js';
import { count } from '../analytics.js';
import { note, drop } from '../resume.js';
import { forgetGame, resultOf as pgnResult, saveGame, syncGames } from '../data/my-games.js';
import { showLevel } from './level-line.js';
import { formatClock } from './clock.js';
import { lichessBase, lichessReturn, startLichess, tokenFor } from '../account/lichess-oauth.js';
import { importText } from '../data/imported.js';
import {
  CHALLENGES, LichessBoard, PLAYABLE, PLAY_KEY, SEEKS, canAccept, clockOf, drawOffered,
  movesOf, normalisedMoves, resultOf,
} from './lichess-board.js';

const $ = (id) => document.getElementById(id);

mountNav($('nav-slot'), { here: 'play', base: '../' });
mountInstall($('install-slot'), { base: '../' });

const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });
const sideWord = (side) => (side === 'w' ? 'White' : 'Black');

// The lobby before the board: it is page, not scene, and there is no game to
// play under it yet, so it should not wait the seconds three.js takes. Connect
// works at once (it only leaves for Lichess); a connected lobby is shown but
// kept inert until the board is there to play on.
{
  const early = readSaved();
  $('connect').hidden = Boolean(early);
  $('connected').hidden = !early;
  if (early) $('who').textContent = early.username;
  $('connected').inert = true;
}
$('connect-go').addEventListener('click', connect);

const table = await createTable({ fair: true });
$('connected').inert = false;
// The six angles as one button over the board. See `../camera-menu.js`.
mountCameraMenu($('views'), { labels: CAMERA_WORDS });
// 2D / 3D to the masthead and full screen to the bar (`../lean-layout.js`).
mountLeanLayout();
const live = table.live;

// ------------------------------------------------------------ the connection

function readSaved() {
  try {
    const saved = JSON.parse(localStorage.getItem(PLAY_KEY) || 'null');
    return saved?.token && saved?.id ? saved : null;
  } catch { return null; }
}

let saved = readSaved();
let api = saved ? new LichessBoard({ token: saved.token }) : null;
let events = null;

/** The game on the board: `{ id, you, full, state, plies, over, result, pending, gone, stream }`. */
let game = null;
/** The seek or challenge being waited on: `{ kind, control, stop }`. */
let waiting = null;
/** What the last game was sought at, for "Play again". */
let lastSeek = null;
/** After a game, the lobby waits to be asked for (New game), so the end can be looked at. */
let lobbyAsked = false;
const incoming = new Map();
const ongoing = new Map();

async function finishConnect(back) {
  if (back.refused) return 'Lichess was not connected: the request was cancelled.';
  if (!back.ok) return 'That connection did not start here. Try again.';
  table.setLead('Connecting to Lichess…');
  try {
    const token = await tokenFor(back);
    const client = new LichessBoard({ token });
    const me = await client.account();
    if (!me?.id) throw new Error('Lichess did not say who you are.');
    saved = { token, id: String(me.id).toLowerCase(), username: me.username || me.id, at: Date.now() };
    localStorage.setItem(PLAY_KEY, JSON.stringify(saved));
    api = client;
    count('lichess-connect');
    return '';
  } catch (err) {
    return err.message || 'Lichess could not be connected.';
  }
}

async function connect() {
  $('connect-go').disabled = true;
  try {
    await startLichess({ scope: 'board:play' });
  } catch {
    $('connect-go').disabled = false;
  }
}

async function disconnect() {
  const client = api;
  forget();
  showConnect('');
  await client?.revoke();
}

/** Stop everything and drop the token — Disconnect, or Lichess refusing it. */
function forget() {
  events?.abort();
  events = null;
  waiting?.stop();
  closeGame();
  try { localStorage.removeItem(PLAY_KEY); } catch { /* blocked */ }
  saved = null;
  api = null;
  incoming.clear();
  ongoing.clear();
}

function lost(err) {
  if (err?.status !== 401) return false;
  forget();
  showConnect(err.message);
  return true;
}

// ------------------------------------------------------------ the lobby

function showConnect(error) {
  $('lobby').hidden = false;
  $('connect').hidden = false;
  $('connected').hidden = true;
  $('connect-go').disabled = false;
  $('connect-error').hidden = !error;
  $('connect-error').textContent = error || '';
  idleBoard();
  reflect();
}

function showConnected() {
  $('connect').hidden = true;
  $('connected').hidden = false;
  $('who').textContent = saved.username;
  reflect();
}

async function idleBoard() {
  table.you = 'w';
  table.state = 'setup';
  table.opponentName = 'Black';
  table.explainMate(false);
  if (live.ply) await table.setScore([]);
  $('clocks').hidden = true;
  $('moves').hidden = true;
}

for (const c of SEEKS) $('seek-control').add(new Option(`${c.label} · ${c.speed}`, c.id));
for (const c of CHALLENGES) $('challenge-control').add(new Option(`${c.label} · ${c.speed}`, c.id));
$('seek-control').value = '15+10';
$('challenge-control').value = '10+0';

$('seek-form').addEventListener('submit', (event) => {
  event.preventDefault();
  seek(SEEKS.find((c) => c.id === $('seek-control').value), $('seek-rated').checked);
});
$('seek-stop').addEventListener('click', () => waiting?.stop());

async function seek(control, rated) {
  if (!api || waiting || (game && !game.over) || !control) return;
  const ctl = new AbortController();
  const mine = { kind: 'seek', control, stop: () => ctl.abort() };
  waiting = mine;
  lastSeek = { control, rated };
  $('seek-note').textContent = `Looking for someone to play ${control.label}${rated ? ', rated' : ''}…`;
  count('lichess-seek', control.id);
  reflect();
  try {
    await api.seek({ minutes: control.minutes, increment: control.increment, rated }, ctl.signal);
    if (!ctl.signal.aborted) {
      // Paired: the game arrives on the event stream. Or the seek lapsed.
      $('seek-note').textContent = 'Found someone — starting…';
      setTimeout(() => {
        if (!game && $('seek-note').textContent.startsWith('Found')) $('seek-note').textContent = 'Nobody was found this time. Try again.';
      }, 6000);
    } else {
      $('seek-note').textContent = mine.matched ? '' : 'Stopped looking.';
    }
  } catch (err) {
    if (!lost(err) && !ctl.signal.aborted) $('seek-note').textContent = err.message;
  } finally {
    if (waiting === mine) waiting = null;
    reflect();
  }
}

$('challenge-form').addEventListener('submit', (event) => {
  event.preventDefault();
  if (waiting?.kind === 'challenge') { waiting.stop(); return; }
  challenge();
});

async function challenge() {
  const user = $('challenge-user').value.trim().replace(/^@/, '');
  const control = CHALLENGES.find((c) => c.id === $('challenge-control').value);
  if (!/^[A-Za-z0-9][A-Za-z0-9_-]{1,29}$/.test(user)) {
    $('challenge-note').textContent = 'That is not a Lichess username.';
    return;
  }
  if (!api || waiting || (game && !game.over) || !control) return;
  const ctl = new AbortController();
  const mine = { kind: 'challenge', control, stop: () => ctl.abort() };
  waiting = mine;
  $('challenge-note').textContent = `Challenge sent to ${user}. Waiting for them to answer…`;
  count('lichess-challenge', control.id);
  reflect();
  try {
    const done = await api.challenge(user, {
      minutes: control.minutes,
      increment: control.increment,
      rated: $('challenge-rated').checked,
      color: $('challenge-color').value,
    }, ctl.signal);
    $('challenge-note').textContent = done === 'accepted' ? `${user} accepted — starting…`
      : done === 'declined' ? `${user} declined.`
        : done === null ? (mine.matched ? '' : 'Challenge cancelled.')
          : `The challenge to ${user} was ${done || 'closed'}.`;
  } catch (err) {
    if (!lost(err)) $('challenge-note').textContent = err.status === 404 ? `There is no Lichess player called ${user}.` : err.message;
  } finally {
    if (waiting === mine) waiting = null;
    reflect();
  }
}

function drawIncoming() {
  const list = [...incoming.values()];
  $('incoming-box').hidden = !list.length;
  // A challenge after a game brings the lobby back up: it cannot wait to be looked for.
  if (list.length && game?.over && !lobbyAsked) { lobbyAsked = true; reflect(); }
  $('incoming').innerHTML = list.map((c) => {
    const verdict = canAccept(c);
    const who = `${c.challenger?.title ? `${c.challenger.title} ` : ''}${c.challenger?.name || c.challenger?.id || 'Somebody'}`;
    const rating = c.challenger?.rating ? ` (${c.challenger.rating})` : '';
    const what = `${c.timeControl?.show || c.speed} ${c.speed} · ${c.rated ? 'rated' : 'casual'}`;
    return `<li class="li-item" data-challenge="${escapeHtml(c.id)}">
      <span class="li-item-what">${escapeHtml(who + rating)}<small>${escapeHtml(verdict.ok ? what : `${what} — ${verdict.why}`)}</small></span>
      ${verdict.ok ? '<button class="ctl primary" type="button" data-act="accept">Accept</button>' : ''}
      <button class="ctl" type="button" data-act="decline">Decline</button>
    </li>`;
  }).join('');
}

$('incoming').addEventListener('click', async (event) => {
  const button = event.target.closest('button[data-act]');
  const id = button?.closest('[data-challenge]')?.dataset.challenge;
  const c = id && incoming.get(id);
  if (!c || !api) return;
  if (button.dataset.act === 'accept' && ((game && !game.over) || waiting)) {
    table.say(waiting ? 'Stop looking for a game first, then accept.' : 'Finish this game first.');
    return;
  }
  button.disabled = true;
  try {
    if (button.dataset.act === 'accept') {
      await api.accept(id);
      count('lichess-accept', c.speed);
    } else {
      await api.decline(id, canAccept(c).reason || 'generic');
    }
    incoming.delete(id);
    drawIncoming();
  } catch (err) {
    if (!lost(err)) table.say(err.message);
    button.disabled = false;
  }
});

/**
 * Whether a game can be played on this board. Lichess says in `compat.board`
 * whether its Board API will take moves for it (it will not for a bullet game
 * started on lichess.org, for one); opening one it refuses is a dead end.
 */
function playableHere(g) {
  return PLAYABLE.has(g.speed) && g.variant?.key === 'standard' && g.compat?.board !== false;
}

function drawGames() {
  const list = [...ongoing.values()].filter((g) => g.gameId !== game?.id || game?.over);
  $('games-box').hidden = !list.length;
  $('games').innerHTML = list.map((g) => {
    const playable = playableHere(g);
    const who = g.opponent?.username || 'Somebody';
    return `<li class="li-item" data-game="${escapeHtml(g.gameId)}">
      <span class="li-item-what">Against ${escapeHtml(who)}<small>${escapeHtml(`${g.speed || ''}${g.isMyTurn ? ' · your move' : ''}`)}</small></span>
      ${playable ? '<button class="ctl primary" type="button" data-act="open">Play</button>'
        : `<a class="ctl" href="https://lichess.org/${escapeHtml(g.gameId)}" target="_blank" rel="noopener">Open on Lichess</a>`}
    </li>`;
  }).join('');
}

$('games').addEventListener('click', (event) => {
  const id = event.target.closest('button[data-act="open"]')?.closest('[data-game]')?.dataset.game;
  if (id && !(game && !game.over)) openGame(id);
});

// ------------------------------------------------------------ events

async function listen() {
  events?.abort();
  const ctl = new AbortController();
  events = ctl;
  let wait = 2000;
  while (!ctl.signal.aborted) {
    try {
      await api.events(onEvent, ctl.signal);
      wait = 2000;
    } catch (err) {
      if (ctl.signal.aborted || lost(err)) return;
      if (err.status === 429) wait = 60000;
    }
    if (ctl.signal.aborted) return;
    await sleep(wait);
    wait = Math.min(wait * 2, 30000);
    // Lichess sends every open challenge again when the stream reopens, so the
    // list starts again empty: one withdrawn while we were away must not stay.
    incoming.clear();
    drawIncoming();
  }
}

function onEvent(event) {
  switch (event?.type) {
    case 'gameStart': {
      const g = event.game || {};
      ongoing.set(g.gameId, g);
      if (playableHere(g) && !refusedGames.has(g.gameId) && !(game && !game.over)) openGame(g.gameId);
      drawGames();
      break;
    }
    case 'gameFinish': {
      ongoing.delete(event.game?.gameId);
      drawGames();
      break;
    }
    case 'challenge': {
      const c = event.challenge;
      const mine = c?.challenger?.id?.toLowerCase() === saved?.id || c?.direction === 'out';
      if (c && !mine) incoming.set(c.id, c);
      drawIncoming();
      break;
    }
    case 'challengeCanceled':
    case 'challengeDeclined': {
      incoming.delete(event.challenge?.id);
      drawIncoming();
      break;
    }
    default:
  }
}

// ------------------------------------------------------------ a game

function closeGame() {
  game?.stream.abort();
  game = null;
  hideOffer();
  $('over').hidden = true;
}

/**
 * Games Lichess would not stream to this board, or that were given up on. The
 * event stream announces every game in progress each time it reconnects; these
 * are not opened by themselves again, only from the list by hand.
 */
const refusedGames = new Set();

async function openGame(id) {
  if (game && !game.over && game.id === id) return;
  // A seek or a challenge that led here is finished with, not cancelled: the
  // game usually arrives before the seek's own answer does.
  if (waiting) {
    waiting.matched = true;
    waiting.stop();
  }
  closeGame();
  const stream = new AbortController();
  const g = { id, you: 'w', full: null, state: null, plies: 0, over: false, result: null, pending: null, gone: null, stream };
  game = g;
  lobbyAsked = false;
  $('lobby').hidden = true;
  $('moves').hidden = false;
  table.state = 'playing';
  table.explainMate(false);
  table.setLead('Opening the game…');
  reflect();
  let refused = false;
  try {
    await api.game(id, (line) => onGameLine(g, line), stream.signal);
  } catch (err) {
    if (stream.signal.aborted || g !== game) return;
    if (lost(err)) return;
    table.say(err.message);
    // Lichess saying no (not found, not yours, not playable here) will say no
    // again: back to the lobby rather than asking every second and a half.
    refused = err.status >= 400 && err.status < 500 && err.status !== 429;
  }
  // The stream ends when the game does. If it ended any other way, pick it up
  // again — unless Lichess refused it, when it goes back to the list.
  if (g === game && !g.over && !stream.signal.aborted) {
    if (refused) {
      refusedGames.add(id);
      closeGame();
      reflect();
    } else {
      await sleep(1500);
      if (g === game && !g.over) {
        game = null;
        openGame(id);
      }
    }
  }
  drawGames();
}

/** How many stream lines are still being played onto the board — for the check. */
let handling = 0;

async function onGameLine(g, line) {
  handling++;
  try {
    await handleLine(g, line);
  } finally {
    handling--;
  }
}

async function handleLine(g, line) {
  if (g !== game) return;
  if (line.type === 'gameFull') {
    g.full = line;
    const me = saved?.id;
    g.you = line.white?.id === me ? 'w' : line.black?.id === me ? 'b' : null;
    if (!g.you || (line.initialFen && line.initialFen !== 'startpos')) {
      closeGame();
      table.say(g.you ? 'That game started from a set position; open it on Lichess.' : 'That game is not yours to play here.');
      $('lobby').hidden = false;
      reflect();
      return;
    }
    table.you = g.you;
    table.opponentName = nameOf(g.you === 'w' ? line.black : line.white);
    $('clock-w-name').textContent = labelOf(line.white, 'w', g.you);
    $('clock-b-name').textContent = labelOf(line.black, 'b', g.you);
    // Yours on the right, theirs on the left, however the colours fall.
    $('clock-w').style.order = g.you === 'w' ? '2' : '1';
    $('clock-b').style.order = g.you === 'w' ? '1' : '2';
    const { moves } = normalisedMoves(movesOf(line.state));
    await table.setScore(moves);
    await table.faceYourSide(true);
    if (!resultOf(line.state).over) count('lichess-game', line.speed || '');
    await applyState(g, line.state);
    return;
  }
  if (line.type === 'gameState') return applyState(g, line);
  if (line.type === 'opponentGone') {
    g.gone = line.gone ? { at: performance.now(), seconds: Number(line.claimWinInSeconds) || 0 } : null;
    drawGone(g);
  }
  return undefined;
}

async function applyState(g, state) {
  g.state = state;
  const { moves } = normalisedMoves(movesOf(state));
  const have = live.uci();
  const same = moves.length === have.length && moves.every((m, i) => m === have[i]);
  const oneMore = moves.length === have.length + 1 && have.every((m, i) => m === moves[i]);
  if (oneMore) await table.applyUci(moves.at(-1));
  else if (!same) await table.setScore(moves);
  g.plies = moves.length;
  g.pending = null;
  table.showMoves();
  showClock(clockOf(state, moves.length));
  const result = resultOf(state);
  if (result.over) return finish(g, result);
  // For the homepage's "pick up where you left off" — see `chess-app/resume.js`.
  // Coming back to this page finds the game by itself (`ongoing`), so all the
  // card needs is that there is one, whom against and how far in.
  note('lichess', { id: g.id, opponent: table.opponentName, moves: g.plies });
  record(g, null);
  if (drawOffered(state, g.you)) {
    offer(`${table.opponentName} offers a draw.`, [
      ['Accept the draw', () => answerDraw(g, true)],
      ['Decline', () => answerDraw(g, false)],
    ], 'draw');
  } else if (offering === 'draw') {
    hideOffer();
  }
  reflect();
  return undefined;
}

async function play(mv) {
  const g = game;
  if (!g || g.over || g.pending) return;
  const uci = uciOf(mv);
  g.pending = uci;
  table.putDown();
  reflect();
  try {
    await api.move(g.id, uci);
    // The stream plays it. If it has not within a few seconds, let the board be picked up again.
    setTimeout(() => { if (g.pending === uci) { g.pending = null; reflect(); } }, 5000);
  } catch (err) {
    if (g !== game) return;
    g.pending = null;
    if (!lost(err)) table.say(`Lichess did not take that move: ${err.message}`);
    reflect();
  }
}

table.canPick = () => Boolean(game && !game.over && !game.pending && game.full && live.turn === game.you);
table.onPick = (mv) => { play(mv); };

async function answerDraw(g, yes) {
  try { await api.draw(g.id, yes); } catch (err) { if (!lost(err)) table.say(err.message); }
}

$('draw').addEventListener('click', async () => {
  const g = game;
  if (!g || g.over) return;
  $('draw').disabled = true;
  try {
    await api.draw(g.id, true);
    table.say('Draw offered.');
  } catch (err) {
    if (!lost(err)) table.say(err.message);
  }
  reflect();
});

$('resign').addEventListener('click', async () => {
  const g = game;
  if (!g || g.over || !confirm('Resign this game?')) return;
  try { await api.resign(g.id); } catch (err) { if (!lost(err)) table.say(err.message); }
});

$('abort').addEventListener('click', async () => {
  const g = game;
  if (!g || g.over) return;
  try { await api.abort(g.id); } catch (err) { if (!lost(err)) table.say(err.message); }
});

function drawGone(g) {
  if (!g.gone) {
    if (offering === 'gone') hideOffer();
    return;
  }
  const left = Math.max(0, Math.ceil(g.gone.seconds - (performance.now() - g.gone.at) / 1000));
  if (left > 0) {
    offer(`${table.opponentName} has left the game. You can claim the win in ${left} seconds.`, [], 'gone');
    setTimeout(() => { if (g === game && g.gone) drawGone(g); }, 1000);
  } else {
    offer(`${table.opponentName} has left the game.`, [
      ['Claim the win', async () => {
        try { await api.claimVictory(g.id); } catch (err) { if (!lost(err)) table.say(err.message); }
      }],
    ], 'gone');
  }
}

/**
 * This game, into your games (`../data/my-games.js`), after every move and at
 * the end. Lichess keeps the game itself; this keeps it in the one list of
 * everything you played here, to review or to find again. An aborted game was
 * never a game, so it comes off the list rather than going on it.
 */
function record(g, result) {
  const id = `l-${g.id.toLowerCase()}`;
  if (result?.aborted) { forgetGame(id); return; }
  if (!g.you) return;
  saveGame({
    id,
    kind: 'lichess',
    lichess: g.id,
    you: g.you,
    white: g.you === 'w' ? 'You' : table.opponentName,
    black: g.you === 'b' ? 'You' : table.opponentName,
    moves: live.game.moves.slice(0, live.ply),
    result: result ? pgnResult(result.winner) : '*',
    by: result?.by || '',
    over: Boolean(result),
    // Their Lichess rating, which is what makes this game count towards your
    // level (`ratedGame` in `../level.js`). None for Lichess's own AI.
    oppRating: Number((g.you === 'w' ? g.full?.black : g.full?.white)?.rating) || undefined,
    speed: g.full?.speed || undefined,
  });
}

/** The game is over: say how, and offer what comes next. */
function finish(g, result) {
  if (g.over) return;
  g.over = true;
  drop('lichess');
  g.result = result;
  record(g, result);
  hideOffer();
  table.state = 'over';
  table.putDown();
  if (result.by === 'Checkmate') table.explainMate(true);
  const them = table.opponentName;
  $('over-line').textContent = result.aborted ? 'Aborted'
    : !result.winner ? 'Drawn'
      : result.winner === g.you ? 'You won' : `${them} won`;
  $('over-why').textContent = `${result.by}.`;
  showLevel($('over-why'), `l-${g.id.toLowerCase()}`);
  const acts = $('over-acts');
  acts.innerHTML = '';
  if (!result.aborted && g.plies >= 4) {
    act(acts, 'Review this game', (button) => review(g, button), true);
  }
  if (lastSeek) {
    act(acts, `Play another ${lastSeek.control.label}`, () => seek(lastSeek.control, lastSeek.rated));
  }
  act(acts, 'New game', () => { lobbyAsked = true; reflect(); });
  const link = document.createElement('a');
  link.className = 'ctl';
  link.href = `https://lichess.org/${encodeURIComponent(g.id)}`;
  link.target = '_blank';
  link.rel = 'noopener';
  link.textContent = 'Open on Lichess';
  acts.appendChild(link);
  $('over').hidden = false;
  lobbyAsked = false;
  count('lichess-end', result.aborted ? 'aborted' : !result.winner ? 'drawn' : result.winner === g.you ? 'won' : 'lost', g.plies);
  drawGames();
  reflect();
}

/**
 * The review: the game from Lichess into this browser's library, as one of your
 * games, and then the player — which reviews an imported game by itself.
 */
async function review(g, button) {
  button.disabled = true;
  button.textContent = 'Getting the game…';
  try {
    const pgn = await api.pgn(g.id);
    const them = table.opponentName;
    const { games } = await importText(`Lichess · ${saved.username} vs ${them}`, pgn, {
      // `newest: 0`, so this one game does not move where "Fetch new games" starts from.
      fetched: { source: 'lichess', user: saved.username, newest: 0 },
    });
    if (!games?.length) throw new Error('The game could not be read.');
    count('lichess-review');
    location.href = `../watch/player.html?game=${encodeURIComponent(games[0].id)}`;
  } catch (err) {
    if (lost(err)) return;
    button.disabled = false;
    button.textContent = 'Review this game';
    table.say(err.message);
  }
}

// ------------------------------------------------------------ the clock

let clock = null;

function showClock(state) {
  clock = state ? { ...state, heard: performance.now() } : null;
  $('clocks').hidden = !clock;
  tick();
}

function timeLeft(side) {
  if (!clock) return 0;
  const since = clock.running === side && !game?.over ? performance.now() - clock.heard : 0;
  return clock.left[side] - since;
}

function tick() {
  if (!clock) return;
  for (const side of ['w', 'b']) {
    const ms = timeLeft(side);
    const face = $(`clock-${side}`);
    $(`clock-${side}-time`).textContent = formatClock(ms);
    face.classList.toggle('on', clock.running === side && !game?.over);
    face.classList.toggle('low', ms < 10000 && ms > 0);
    face.classList.toggle('out', ms <= 0);
  }
}
setInterval(tick, 100);

// ------------------------------------------------------------ what it says

let offering = null;

function offer(text, choices, kind) {
  offering = kind;
  $('offer-line').textContent = text;
  const acts = $('offer-acts');
  acts.innerHTML = '';
  for (const [label, onClick] of choices) act(acts, label, () => { hideOffer(); onClick(); });
  $('offer').hidden = false;
}

function hideOffer() {
  offering = null;
  $('offer').hidden = true;
}

function act(into, label, onClick, primary = false) {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = primary ? 'ctl primary' : 'ctl';
  button.textContent = label;
  button.addEventListener('click', () => onClick(button));
  into.appendChild(button);
}

function nameOf(player) {
  return player?.name || player?.id || (player?.aiLevel ? `Stockfish level ${player.aiLevel}` : 'Your opponent');
}

function labelOf(player, side, you) {
  const rating = player?.rating ? ` ${player.rating}` : '';
  return side === you ? `You${rating}` : `${nameOf(player)}${rating}`;
}

function reflect() {
  const g = game;
  const playing = Boolean(g && !g.over && g.full);
  const yourMove = playing && live.turn === g.you;
  const myOffer = playing && (g.you === 'w' ? g.state?.wdraw : g.state?.bdraw);

  if (g?.full) {
    const f = g.full;
    const control = f.clock ? `${f.clock.initial / 60000}+${f.clock.increment / 1000}` : f.speed;
    table.setPills([
      `vs ${table.opponentName}`,
      `You are ${sideWord(g.you)}`,
      `${control} ${f.speed || ''}`.trim(),
      f.rated ? 'Rated' : 'Casual',
    ]);
  } else {
    table.setPills(saved ? [`Lichess · ${saved.username}`] : ['Lichess']);
  }

  table.setTag(
    !api ? 'not connected'
      : g && !g.over ? (g.pending ? 'sending' : yourMove ? 'your move' : g.full ? 'their move' : 'opening')
        : waiting?.kind === 'seek' ? 'looking'
          : waiting?.kind === 'challenge' ? 'waiting'
            : g?.over ? 'game over' : 'ready',
    Boolean(waiting) || Boolean(g?.pending),
  );

  table.setLead(
    !api ? 'Connect your Lichess account to play'
      : g && !g.over ? (g.pending ? 'Sending your move to Lichess…'
        : yourMove ? 'Your move' : g.full ? `${table.opponentName} is thinking` : 'Opening the game…')
        : waiting?.kind === 'seek' ? 'Looking for an opponent on Lichess…'
          : waiting?.kind === 'challenge' ? 'Waiting for an answer to your challenge…'
            : g?.over ? 'The game is over' : 'Find a game, or challenge someone',
  );

  const early = playing && g.plies < 2;
  $('abort').hidden = !early;
  $('resign').disabled = !playing || early;
  $('draw').disabled = !playing || early || Boolean(myOffer);
  $('draw').textContent = myOffer ? 'Draw offered' : 'Offer a draw';

  const busy = Boolean(waiting) || playing;
  $('seek-go').hidden = waiting?.kind === 'seek';
  $('seek-go').disabled = busy;
  $('seek-stop').hidden = waiting?.kind !== 'seek';
  $('challenge-go').textContent = waiting?.kind === 'challenge' ? 'Cancel challenge' : 'Send challenge';
  $('challenge-go').disabled = (busy && waiting?.kind !== 'challenge');
  // The lobby is over the page whenever there is no game: before one, and after
  // one once New game is pressed — or once another is being looked for, so
  // Stop looking is in reach. After a game it can be put away again.
  $('lobby').hidden = Boolean(g) && !(g.over && (lobbyAsked || waiting));
  $('lobby-close').hidden = !g?.over || Boolean(waiting);
}

addEventListener('beforeunload', (event) => {
  if (game && !game.over) event.preventDefault();
});

$('disconnect').addEventListener('click', disconnect);
$('lobby-close').addEventListener('click', () => { lobbyAsked = false; reflect(); });

// ------------------------------------------------------------ and go

async function start() {
  const back = lichessReturn();
  if (back) {
    const error = await finishConnect(back);
    if (error) return showConnect(error);
  }
  if (!api) return showConnect('');
  showConnected();
  idleBoard();
  listen();
  return undefined;
}

syncGames();
await start();

// What `scripts/check-lichess-play.mjs` drives the page through.
window.__lichess = {
  live,
  base: lichessBase(),
  get connected() { return Boolean(api); },
  get busy() { return handling > 0; },
  get game() {
    return game && {
      id: game.id, you: game.you, over: game.over, result: game.result, plies: game.plies, pending: game.pending,
    };
  },
  get waiting() { return waiting && { kind: waiting.kind, control: waiting.control.id }; },
  get incoming() { return [...incoming.keys()]; },
  get clock() { return clock && { ...clock, w: timeLeft('w'), b: timeLeft('b') }; },
  play: (uci) => {
    const mv = live.fromUci(uci);
    if (!mv) throw new Error(`${uci} is not legal here`);
    if (!table.canPick()) throw new Error('not your move');
    return play(mv);
  },
};
